import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import KNFormRadioGroup from 'components/KNForm/KNFormRadioGroup'
import { processDefaultValues, processServerErrorMessages } from 'global/helpers/form'
import type { WithId } from 'global/helpers/requests'
import { type ReactElement, useEffect } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import CompanyTypeLabel from 'screens/CompanyManager/CompanyTypeLabel'
import { revokeRole, USERS_QUERY_KEY } from './UserManager.service'
import type { RevokeRoleFormValues, UserData } from './UserManager.types'

interface RevokeRoleDialogPayload {
  readonly user: UserData
}

interface RevokeRoleDialogProps {
  readonly payload: RevokeRoleDialogPayload
  readonly open: boolean
  readonly onAction: () => void
  readonly onClose: () => void
}

const RevokeRoleDialog = ({ payload, open, onAction, onClose }: RevokeRoleDialogProps): ReactElement => {
  const { t } = useTranslation()
  const { handleSubmit, watch, reset, control, formState, setValue, setError } = useForm<RevokeRoleFormValues>()
  const queryClient = useQueryClient()
  const revokeRoleMutation = useMutation({
    mutationFn: (data: WithId<RevokeRoleFormValues>) => revokeRole(data.id, data),
    onError: (error) => setError('root', processServerErrorMessages(error)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] }),
  })
  const watchedCompanyCid = watch('companyCid')

  useEffect(() => {
    if (open) {
      reset(
        processDefaultValues({
          companyCid: null,
          role: null,
        }),
      )
    }
  }, [open])

  useEffect(() => {
    if (watchedCompanyCid) {
      setValue('role', payload.user.roles[watchedCompanyCid]!)
    }
  }, [watchedCompanyCid])

  const onSubmit: SubmitHandler<RevokeRoleFormValues> = async (data: RevokeRoleFormValues) => {
    await revokeRoleMutation.mutateAsync({ id: payload.user.cid, ...data })
    onAction()
  }

  return (
    <KNDialog
      open={open}
      onClose={onClose}
      closeLabel={t('general.cancel')}
      preventClosing={formState.isSubmitting}
      title={`${payload.user.displayName} — ${t('screens.shared.user_manager.revoke_role.revoke_role')}`}
      actions={
        <Button
          type="submit"
          color="secondary"
          variant="contained"
          loading={formState.isSubmitting}
          onClick={handleSubmit(onSubmit)}
        >
          {t('screens.shared.user_manager.revoke_role.revoke')}
        </Button>
      }
      onSubmit={handleSubmit(onSubmit)}
    >
      <KNDialogFormErrors errors={formState.errors?.root} />
      <Stack spacing={2}>
        <KNFormRadioGroup
          name="companyCid"
          label={t('screens.shared.user_manager.revoke_role.company')}
          control={control}
          rules={{
            required: t('form.validation.required'),
          }}
          options={payload.user.companies.map((company) => ({
            value: company.cid,
            label: (
              <Stack spacing={1} direction="row" alignItems="center">
                <CompanyTypeLabel type={company.type} compact />
                <Box>{company.displayName}</Box>
                <Typography variant="p6" color="primary.light">
                  {t(`screens.shared.user_manager.roles.${payload.user.roles[company.cid]}`)}
                </Typography>
              </Stack>
            ),
          }))}
        />
      </Stack>
    </KNDialog>
  )
}

export default RevokeRoleDialog
